import axios from "axios";
import { URL_FORMS } from "constants/urlApis";
import { endpointError } from "utils/codeErrorsTimeout";
import { isNullOrUndefined } from "utils/validations";
import useLogin from "hooks/useLogin";

export default function useForm() {
  const { jwt } = useLogin();
  const headers = {
    Authorization: `Bearer ${jwt}`,
  };

  /**
   * Fetches a list of forms from the server with optional filters for status, search, and actions.
   * This function sends a GET request to the forms endpoint with pagination, search, and filtering options.
   * If a `status` is provided, it includes that in the request query; otherwise, it omits the `status` filter.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of forms per page.
   * @param {string} search - The search keyword to filter forms by name.
   * @param {string|null} status - The status filter for forms (optional).
   * @param {string} actions - The action filter to filter forms by actions.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the list of forms.
   */
  const getForms = async (page, per_page, search, status, actions) => {
    let response;
    if (status) {
      response = await axios.get(
        `${URL_FORMS}/form/forms/?status=${status}&action=${actions}&operation=and&name=${search}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.forms.getForms,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS}/form/forms/?name=${search}&action=${actions}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.forms.getForms,
        }
      );
    }
    return response.data;
  };

  /**
   * Fetches available forms for a specific user based on the provided parameters.
   * This function sends a GET request to the `availables-per-user` endpoint, with optional filtering by status, actions, and search query.
   * Pagination is controlled through the `page` and `per_page` parameters.
   * @param {number} page - The current page number for pagination.
   * @param {number} per_page - The number of items per page for pagination.
   * @param {string} search - The search query to filter forms by name.
   * @param {string|null} status - The status to filter forms (optional). If `null`, the status filter is ignored.
   * @param {string} actions - The actions to filter forms.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the forms available for the user.
   */
  const getFormsAvailablesPerUser = async (page, per_page, search, status, actions) => {
    let response;
    if (isNullOrUndefined(status) === false) {
      response = await axios.get(
        `${URL_FORMS}/form/forms/availables-per-user/?status=${status}&action=${actions}&operation=and&page=${page}&per_page=${per_page}&name=${search}`,
        {
          headers,
          endpointError: endpointError.forms.getFormsAvailablesPerUser,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS}/form/forms/availables-per-user/?action=${actions}&page=${page}&per_page=${per_page}&name=${search}`,
        {
          headers,
          endpointError: endpointError.forms.getFormsAvailablesPerUser,
        }
      );
    }
    return response.data;
  };

  /**
   * Creates a new form with the specified data.
   * This function sends a POST request to the `forms` endpoint to create a new form using the provided `formData`.
   * @param {Object} formData - The data to create the new form. This should include all required fields for the form.
   * @param {string} formData.name - The name of the form.
   * @param {string} formData.description - The description of the form.
   * @param {string} formData.status - The status of the form (e.g., active, inactive).
   * @returns {Promise<Object>} A promise that resolves to the response object from the API containing the newly created form details.
   */
  const createForm = async (formData) => {
    const response = await axios.post(`${URL_FORMS}/form/forms/`, formData, {
      headers,
      endpointError: endpointError.forms.createForm,
    });
    return response;
  };

  /**
   * Updates an existing form with the specified data.
   * This function sends a PUT request to the `forms` endpoint to update an existing form identified by the `id` with the provided `form` data.
   * @param {Object} form - The updated data for the form. This should include all fields that need to be updated.
   * @param {string} form.name - The updated name of the form.
   * @param {string} form.description - The updated description of the form.
   * @param {string} form.status - The updated status of the form (e.g., active, inactive).
   * @param {string} id - The unique identifier of the form to be updated.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API containing the updated form details.
   */
  const updateForm = async (form, id) => {
    const response = await axios.put(`${URL_FORMS}/form/forms/${id}/`, form, {
      headers,
      endpointError: endpointError.forms.updateForm,
    });
    return response;
  };

  /**
   * Creates a new field with the provided data.
   * This function sends a POST request to the `fields` endpoint to create a new field with the given `fieldData`.
   * @param {Object} fieldData - The data for the new field. This should include all necessary fields required by the API.
   * @param {string} fieldData.name - The name of the field.
   * @param {string} fieldData.type - The type of the field (e.g., text, number, date).
   * @param {boolean} [fieldData.required=false] - Whether the field is required. Defaults to false.
   * @param {string} [fieldData.placeholder] - An optional placeholder for the field.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API containing the created field details.
   */
  const createFields = async (fieldData) => {
    const response = await axios.post(`${URL_FORMS}/form/fields/`, fieldData, {
      headers,
      endpointError: endpointError.forms.createFields,
    });
    return response;
  };

  /**
   * Retrieves fields from a specific form with optional search functionality.
   * This function sends a GET request to the `fields` endpoint of a specified form. It supports retrieving fields with or without a search parameter.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page for pagination.
   * @param {string} formId - The ID of the form from which to retrieve fields.
   * @param {string} actions - The actions to filter the fields by.
   * @param {boolean} searchField - Whether to include a search query in the request. If true, only fields matching the search criteria are returned.
   * @returns {Promise<Object[]>} A promise that resolves to an array of field objects retrieved from the API.
   */
  const getFields = async (page, per_page, formId, actions, searchField) => {
    let response;
    if (searchField === true) {
      response = await axios.get(
        `${URL_FORMS}/form/forms/${formId}/fields/?search_field=${searchField}&action=${actions}`,
        {
          headers,
          endpointError: endpointError.forms.getFields,
        }
      );
      return response.data;
    } else {
      const response = await axios.get(
        `${URL_FORMS}/form/forms/${formId}/fields/?action=${actions}&page=${page}&per_page=${per_page}`,
        {
          headers,
          endpointError: endpointError.forms.getFields,
        }
      );
      return response.data;
    }
  };

  /**
   * Retrieves a specific form by its ID.
   * This function sends a GET request to retrieve details of a specific form using its ID and optional actions parameter.
   * @param {string} formId - The ID of the form to retrieve.
   * @param {string} actions - The actions to filter the form by.
   * @returns {Promise<Object>} A promise that resolves to the form object retrieved from the API.
   */
  const getFormsById = async (formId, actions) => {
    const response = await axios.get(`${URL_FORMS}/form/forms/${formId}/?action=${actions}`, {
      headers,
      endpointError: endpointError.forms.getFormsById,
    });
    return response.data;
  };

  /**
   * Deletes a specific field from a form.
   * This function sends a DELETE request to remove a specific field from a form using the form ID and field ID.
   * @param {string} formId - The ID of the form from which the field will be deleted.
   * @param {string} fieldId - The ID of the field to be deleted.
   * @returns {Promise<Object>} A promise that resolves to the response object from the API.
   */
  const deleteFields = async (formId, fieldId) => {
    const response = await axios.delete(`${URL_FORMS}/form/forms/${formId}/fields/${fieldId}/`, {
      headers,
      endpointError: endpointError.forms.deleteFields,
    });
    return response;
  };

  /**
   * Retrieves details of a specific field from a form.
   * This function sends a GET request to fetch details of a specific field using its ID.
   * @param {string} fieldId - The ID of the field to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the data of the requested field.
   */
  const getField = async (fieldId) => {
    const response = await axios.get(`${URL_FORMS}/form/field/${fieldId}/`, {
      headers,
      endpointError: endpointError.forms.getField,
    });
    return response.data.data;
  };

  /**
   * Updates the order of a specific field in a form.
   * This function sends a PATCH request to update the order of a field using its ID.
   * @param {string} fieldId - The ID of the field whose order needs to be updated.
   * @param {Object} data - The data containing the new order information for the field.
   * @param {number} data.newOrder - The new order value to be assigned to the field.
   * @returns {Promise<Object>} A promise that resolves to the response of the update request.
   */
  const updateOrder = async (fieldId, data) => {
    const response = axios.patch(`${URL_FORMS}/form/field/${fieldId}/order/`, data, {
      headers,
      endpointError: endpointError.forms.updateOrder,
    });

    return response;
  };

  /**
   * Updates a specific field in a form.
   * This function sends a PATCH request to update a field using its ID.
   * @param {string} fieldId - The ID of the field to be updated.
   * @param {Object} data - The data to update the field with.
   * @param {string} data.name - The new name of the field (optional).
   * @param {string} data.type - The new type of the field (optional).
   * @param {string} data.value - The new value of the field (optional).
   * @param {boolean} [data.required] - Whether the field is required or not (optional).
   * @param {number} [data.order] - The new order of the field (optional).
   * @returns {Promise<Object>} A promise that resolves to the response of the update request.
   */
  const updateField = async (fieldId, data) => {
    const response = await axios.patch(`${URL_FORMS}/form/field/${fieldId}/`, data, {
      headers,
      endpointError: endpointError.forms.updateField,
    });
    return response;
  };

  /**
   * Deletes a specific form using its ID.
   * This function sends a DELETE request to remove the form with the given ID.
   * @param {string} formId - The ID of the form to be deleted.
   * @returns {Promise<Object>} A promise that resolves to the response of the delete request.
   */
  const deleteForm = async (formId) => {
    const response = await axios.delete(`${URL_FORMS}/form/forms/${formId}/`, {
      headers,
      endpointError: endpointError.forms.deleteForm,
    });
    return response;
  };

  /**
   * Cancels a specific record in a form using its UUID and the record's UUID.
   * This function sends a POST request to cancel a record with the given UUID within the specified form.
   * @param {string} form_uuid - The UUID of the form containing the record.
   * @param {string} record_uuid - The UUID of the record to be canceled.
   * @param {Object} data - The data to be sent in the request body, typically containing cancellation details.
   * @returns {Promise<Object>} A promise that resolves to the response of the POST request.
   */
  const annulmentRecord = async (form_uuid, record_uuid, data) => {
    const response = await axios.post(
      `${URL_FORMS}/form/forms/${form_uuid}/record/${record_uuid}/cancel/`,
      data,
      {
        headers,
        endpointError: endpointError.forms.annulmentRecord,
      }
    );
    return response;
  };

  /**
   * Retrieves configuration fields for a specific process task in a workflow, optionally filtered by an external form.
   * This function sends a GET request to obtain the fields configuration associated with a given process task ID,
   * and can include an external form filter if provided.
   * @param {string} porcessTaskId - The ID of the process task for which to retrieve configuration fields.
   * @param {string} [externalForm] - Optional parameter. The external form to filter the fields configuration.
   * @returns {Promise<Object>} A promise that resolves to the response of the GET request.
   */
  const getFieldsStepWorkflowConfig = async (porcessTaskId, externalForm) => {
    const response = await axios.get(
      `${URL_FORMS}/workflows/process-task/${porcessTaskId}/configuration-fields-form/?external_form=${externalForm}`,
      {
        headers,
        endpointError: endpointError.forms.getFieldsStepWorkflowConfig,
      }
    );
    return response;
  };

  /**
   * Updates the configuration of fields for a specific process task in a workflow.
   * This function sends a PATCH request to update the fields configuration associated with a given process task ID.
   * The `configuredStep` parameter includes the updated configuration details.
   * @param {string} porcessTaskId - The ID of the process task for which to update the fields configuration.
   * @param {Object} configuredStep - The updated configuration details for the fields.
   * @param {Object} configuredStep.field1 - Description of field1 in the configuration.
   * @param {Object} configuredStep.field2 - Description of field2 in the configuration.
   * @param {Object} [configuredStep.fieldN] - Optional additional fields in the configuration.
   * @returns {Promise<Object>} A promise that resolves to the response of the PATCH request.
   */
  const updateFieldsStepWorkFlowConfig = async (porcessTaskId, configuredStep) => {
    const response = await axios.patch(
      `${URL_FORMS}/workflows/process-task/${porcessTaskId}/filling-in-form-fields-from-task/`,
      configuredStep,
      {
        headers,
        endpointError: endpointError.forms.updateFieldsStepWorkFlowConfig,
      }
    );
    return response;
  };

  /**
   * Creates a record configuration for a specific process task in a workflow.
   * This function sends a POST request to create a new record configuration associated with a given process task ID.
   * The `configuredStep` parameter includes the details for the new record configuration.
   * @param {string} porcessTaskId - The ID of the process task for which to create the record configuration.
   * @param {Object} configuredStep - The details for the new record configuration.
   * @param {Object} configuredStep.field1 - Description of field1 in the configuration.
   * @param {Object} configuredStep.field2 - Description of field2 in the configuration.
   * @param {Object} [configuredStep.fieldN] - Optional additional fields in the configuration.
   * @returns {Promise<Object>} A promise that resolves to the response of the POST request.
   */
  const createRecordStepWorkFlowConfig = async (porcessTaskId, configuredStep) => {
    const response = await axios.post(
      `${URL_FORMS}/workflows/process-task/${porcessTaskId}/create-record-from-task/`,
      configuredStep,
      {
        headers,
        endpointError: endpointError.forms.createRecordStepWorkFlowConfig,
      }
    );
    return response;
  };

  /**
   * Retrieves a list of forms available for lookup associated with a specific form ID.
   * This function sends a GET request to fetch forms that can be used for lookup purposes based on the provided form ID.
   * @param {string} formId - The ID of the form for which to retrieve lookup options.
   * @returns {Promise<Object[]>} A promise that resolves to an array of form objects available for lookup.
   */
  const getFormsForLookup = async (formId) => {
    const response = await axios.get(`${URL_FORMS}/form/forms/${formId}/forms-as-lookup/`, {
      headers,
      endpointError: endpointError.forms.getFormsForLookup,
    });
    return response.data;
  };

  /**
   * Retrieves a list of fields associated with a specific form ID that can be used for lookup.
   * This function sends a GET request to fetch fields from a form that are available for lookup purposes based on the provided form ID.
   * @param {string} formId - The ID of the form for which to retrieve lookup fields.
   * @returns {Promise<Object[]>} A promise that resolves to an array of field objects available for lookup.
   */
  const getFieldsFormsForLookup = async (formId) => {
    const response = await axios.get(`${URL_FORMS}/form/forms/${formId}/form-as-lookup-fields/`, {
      headers,
      endpointError: endpointError.forms.getFieldsFormsForLookup,
    });
    return response.data;
  };

  /**
   * Retrieves records associated with a specific form ID that match the given search criteria and pagination settings.
   * This function sends a GET request to fetch records based on the form ID, with pagination and search value applied.
   * @param {string} formId - The ID of the form for which to retrieve records.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of records per page.
   * @param {string} [search] - An optional search term to filter records.
   * @returns {Promise<Object[]>} A promise that resolves to an array of record objects matching the search criteria.
   */
  const getRecordsByFormLookup = async (formId, page, per_page, search) => {
    const response = await axios.get(
      `${URL_FORMS}/form/forms/${formId}/form-lookup-to-record/?page=${page}&per_page=${per_page}&record_value=${search}`,
      {
        headers,
        endpointError: endpointError.forms.getRecordsByFormLookup,
      }
    );
    return response.data;
  };

  /**
   * Updates the workflow configuration for filling in form fields in batch mode.
   * This function sends a PATCH request to update multiple workflow configurations based on the provided batch data.
   * @param {Object} configuredStep - The configuration data to update for the workflow steps in batch mode.
   * @param {Object} configuredStep.data - The specific configuration details to be updated.
   * @param {string} configuredStep.data.someField - Example field to include in the configuration data.
   * @returns {Promise<Object>} A promise that resolves to the response object from the server.
   * @throws {Error} Throws an error if the request fails or if the API returns an error response.
   */
  const updateFieldsStepWorkFlowConfigBatch = async (configuredStep) => {
    const response = await axios.patch(
      `${URL_FORMS}/workflows/filling-in-form-fields-from-task/accomplish-batch/`,
      configuredStep,
      {
        headers,
        endpointError: endpointError.forms.updateFieldsStepWorkFlowConfigBatch,
      }
    );
    return response;
  };

  /**
   * Retrieves fields associated with a form based on a specific record sticker.
   * @async
   * @param {string} formId - The unique identifier of the form.
   * @returns {Promise<object>} A promise that resolves with the data containing fields associated with the specified form and record sticker.
   */
  const getFieldsBySticker = async (formId) => {
    const response = await axios.get(`${URL_FORMS}/form/forms/${formId}/fields/record-sticker/`, {
      headers,
      endpointError: endpointError.forms.getFieldsBySticker,
    });
    return response.data;
  };

  /**
   * Exports records for a specific form by making an HTTP POST request.
   * @param {string} recordId - The ID of the record to be exported.
   * @returns {Promise<Object>} - A promise that resolves to the response object from the export API.
   */
  const exportRecords = async (recordId, dataExportedRecords, timeZone) => {
    const response = await axios.post(
      `${URL_FORMS}/exports/forms/${recordId}/records/?time_zone=${timeZone}`,
      dataExportedRecords,
      {
        headers,
        endpointError: endpointError.forms.exportRecords,
      }
    );
    return response;
  };

  /**
   * Fetches the exported records from the server based on pagination, search query, and the number of records per page.
   * @param {number} page - The page number for pagination.
   * @param {string} search - The search query string to filter the records.
   * @param {number} per_page - The number of records to fetch per page.
   * @returns {Promise<Object>} - A promise that resolves to the response object containing the exported records.
   * @throws {Error} - Throws an error if the request fails.
   */
  const getExportRecords = async (page, status, per_page, searchExport) => {
    let response;
    if (isNullOrUndefined(status) === false) {
      response = await axios.get(
        `${URL_FORMS}/exports/forms/records/?page=${page}&status=${status}&per_page=${per_page}&search=${searchExport}`,
        {
          headers,
          endpointError: endpointError.forms.exportRecords,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS}/exports/forms/records/?page=${page}&per_page=${per_page}&search=${searchExport}`,
        {
          headers,
          endpointError: endpointError.forms.getExportRecords,
        }
      );
    }
    return response.data;
  };

  /**
   * Sends a POST request to create a new form template.
   * @param {Object} formData - The data for the form template to be created.
   * @returns {Promise<Object>} The response from the server.
   */
  const createTemplate = async (formData) => {
    const response = await axios.post(`${URL_FORMS}/form/templates/`, formData, {
      headers,
      endpointError: endpointError.forms.createTemplate,
    });
    return response;
  };

  /**
   * Fetches all form templates with optional filtering by status and search name.
   * @param {number} page - The page number for pagination.
   * @param {number} per_page - The number of items per page.
   * @param {string} status - The status to filter templates by.
   * @param {string} searchName - The name to search templates by.
   * @returns {Promise<Object>} The response data containing the templates.
   */
  const getAllTemplates = async (page, per_page, status, searchName) => {
    let response;
    if (isNullOrUndefined(status) === false) {
      response = await axios.get(
        `${URL_FORMS}/form/templates/?page=${page}&per_page=${per_page}&status=${status}`,
        {
          headers,
          endpointError: endpointError.forms.exportRecords,
        }
      );
    } else {
      response = await axios.get(
        `${URL_FORMS}/form/templates/?page=${page}&per_page=${per_page}&name=${searchName}&form_name=${searchName}`,
        {
          headers,
          endpointError: endpointError.forms.getAllTemplates,
        }
      );
    }
    return response.data;
  };

  /**
   * Fetches a template by its ID from the forms API.
   * @param {string} templateId - The ID of the template to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the template data.
   * @throws Will throw an error if the request fails.
   */
  const getTemplateById = async (templateId) => {
    const response = await axios.get(`${URL_FORMS}/form/templates/${templateId}/`, {
      headers,
      endpointError: endpointError.forms.getTemplateById,
    });
    return response;
  };

  /**
   * Updates a template with the specified template ID using the provided form data.
   * Sends a PATCH request to the URL_FORMS endpoint to update the template.
   * The request includes custom headers and handles endpoint-specific errors.
   * @param {string} templateId - The ID of the template to update.
   * @param {Object} formData - The data to update the template with.
   * @returns {Promise<Object>} The updated template data from the response.
   */
  const updateTemplate = async (templateId, formData) => {
    const response = await axios.patch(`${URL_FORMS}/form/templates/${templateId}/`, formData, {
      headers,
      endpointError: endpointError.forms.updateTemplate,
    });
    return response;
  };

  /**
   * Fetches the template associated with a specific form.
   * @param {string} formUUid - The unique identifier of the form.
   * @returns {Promise<Object>} The response containing the template data.
   * @throws Will throw an error if the request fails.
   */
  const getTemplateByForm = async (formUUid, page, per_page) => {
    const response = await axios.get(
      `${URL_FORMS}/form/forms/${formUUid}/templates/?page=${page}&per_page=${per_page}`,
      {
        headers,
        endpointError: endpointError.forms.getTemplateByForm,
      }
    );
    return response.data;
  };

  /**
   * Generates a document template by sending a POST request to the server.
   * @param {Object} templateData - The data for the template to be generated.
   * @param {string} timeZone - The time zone to be used for the template generation.
   * @returns {Promise<Object>} The server response containing the generated template.
   */
  const generateDocumentTemplate = async (templateData, timeZone) => {
    const response = await axios.post(
      `${URL_FORMS}/form/templates/generate/?time_zone=${timeZone}`,
      templateData,
      {
        headers,
        endpointError: endpointError.forms.generateDocumentTemplate,
      }
    );
    return response;
  };

  /**
   * Fetches a paginated list of web forms from the backend.
   * Executes a GET request against `${URL_FORMS}/form/web_forms/` with the provided
   * page and per_page query parameters, using default headers and
   * endpointError.forms.getWebForms for centralized error handling.
   * @param {number} page - The page index to retrieve.
   * @param {number} per_page - The number of items per page.
   * @returns {Promise<any>} Resolves with the response data (forms and pagination metadata).
   * @throws Propagates request errors handled via endpointError.forms.getWebForms.
   */
  const getPublicWebForms = async (page, per_page, search) => {
    const response = await axios.get(
      `${URL_FORMS}/form/public-web-forms-config/?page=${page}&per_page=${per_page}&name=${search}`,
      {
        headers,
        endpointError: endpointError.forms.getPublicWebForms,
      }
    );
    return response.data;
  };

  /**
   * Creates a webform by sending a POST request to the forms API.
   * Posts the provided payload to `${URL_FORMS}/form/web_forms/generate/` using axios,
   * applying headers and endpointError.forms.createWebform for error handling.
   * @async
   * @param {Object} webformData - Payload for webform creation.
   * @returns {Promise<import('axios').AxiosResponse>} Resolves with the HTTP response.
   * @throws {Error} If the request fails.
   */
  const createPublicWebFormConfiguration = async (webformData) => {
    const response = await axios.post(`${URL_FORMS}/form/public-web-forms-config/`, webformData, {
      headers,
      endpointError: endpointError.forms.createPublicWebFormConfiguration,
    });
    return response;
  };

  /**
   * Deletes a web form by ID via the Forms API.
   * Sends a DELETE request to `${URL_FORMS}/form/web_forms/{formId}/` with standard headers
   * and endpointError metadata for centralized error handling.
   * @param {string|number} formId - Target web form ID.
   * @returns {Promise<import('axios').AxiosResponse>} Resolves with the Axios response.
   * @throws {Error} If the request fails or the server responds with an error status.
   */
  const deletePublicWebFormByUUID = async (formId) => {
    const response = await axios.delete(`${URL_FORMS}/form/public-web-forms-config/${formId}/`, {
      headers,
      endpointError: endpointError.forms.deletePublicWebFormByUUID,
    });
    return response;
  };

  /**
   * Updates a public web form by UUID.
   * Sends a PATCH request to `${URL_FORMS}/form/web_forms/generate/:publicFormUuid` with the provided payload,
   * including standard headers and endpoint-specific error handling via `endpointError.forms.updateWebForm`.
   * @async
   * @param {string} publicFormUuid - Public UUID of the target web form.
   * @param {object} webFormData - Payload containing the form fields to update.
   * @returns {Promise<import('axios').AxiosResponse>} Resolves with the server response.
   */
  const updatePublicWebFormByUUID = async (publicFormUuid, webFormData) => {
    const response = await axios.patch(
      `${URL_FORMS}/form/public-web-forms-config/${publicFormUuid}/`,
      webFormData,
      {
        headers,
        endpointError: endpointError.forms.updatePublicWebFormByUUID,
      }
    );
    return response;
  };

  return {
    getForms,
    createForm,
    updateForm,
    getFormsById,
    createFields,
    getFields,
    deleteFields,
    getField,
    deleteForm,
    updateField,
    updateOrder,
    annulmentRecord,
    getFieldsStepWorkflowConfig,
    updateFieldsStepWorkFlowConfig,
    createRecordStepWorkFlowConfig,
    getFormsForLookup,
    getFieldsFormsForLookup,
    getRecordsByFormLookup,
    getFormsAvailablesPerUser,
    updateFieldsStepWorkFlowConfigBatch,
    getFieldsBySticker,
    exportRecords,
    getExportRecords,
    createTemplate,
    getAllTemplates,
    getTemplateById,
    updateTemplate,
    getTemplateByForm,
    generateDocumentTemplate,
    getPublicWebForms,
    createPublicWebFormConfiguration,
    deletePublicWebFormByUUID,
    updatePublicWebFormByUUID,
  };
}
